<template>
  <div>
    <transition name="splash-fade">
      <div v-if="showSplash" class="splash-screen">
        <!-- Top Navigation -->
        <div class="splash-nav">
          <router-link to="/" class="splash-nav-link font-medium">
            Waitlist
          </router-link>
          <a href="/visa" class="splash-nav-link font-medium"> Visa Checker </a>
        </div>

        <div class="splash-logo-container">
          <!-- Text: welcome back framing -->
          <div class="splash-text">
            <span class="splash-text-line text-is">Welcome back.</span>
            <span class="splash-text-line"
              ><span class="text-tern">Tern</span
              ><span class="text-is"> is now part of </span
              ><span class="text-tilly">Tilly</span
              ><span class="text-is">.</span></span
            >
          </div>

          <!-- Logo transition container -->
          <div class="logo-transition-container">
            <!-- Tern Logo -->
            <img
              src="@/assets/logo/tern.svg"
              alt="Tern Logo"
              class="splash-logo splash-logo-tern"
            />
            <!-- Arrow -->
            <div class="arrow-container">
              <svg
                class="arrow"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M5 12H19M19 12L12 5M19 12L12 19"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </div>
            <!-- Tilly Logo -->
            <img
              src="@/assets/logo/logo.svg"
              alt="Tilly Logo"
              class="splash-logo splash-logo-tilly"
            />
          </div>

          <!-- Sparkle (multi-point iconic flourish) -->
          <div class="sparkle-wrapper">
            <img
              src="@/assets/intro/yellow_stars.svg"
              alt="Sparkle1"
              class="splash-sparkle splash-sparkle-1"
            />
            <img
              src="@/assets/intro/yellow_stars.svg"
              alt="Sparkle2"
              class="splash-sparkle splash-sparkle-2"
            />
          </div>

          <!-- Password Input (only shown if not authenticated) -->
          <transition name="code-slide-up">
            <div v-if="showPasswordInput" class="code-section">
              <!-- Info note -->
              <div class="code-note">
                <p class="code-note-text">
                  Search your inbox for
                  <span class="code-note-subject">“Tern is now Tilly ✨”</span>
                  to find your access code.
                </p>
              </div>

              <div class="code-input-wrapper">
                <input
                  ref="codeInput"
                  v-model="code"
                  type="password"
                  class="code-input"
                  placeholder="enter your access code"
                  @keyup.enter="handleSubmit"
                  @input="handleInput"
                  @focus="isFocused = true"
                  @blur="isFocused = false"
                />
                <div
                  class="code-underline"
                  :class="{ active: isFocused }"
                ></div>
              </div>

              <!-- Error message -->
              <transition name="error-fade">
                <div v-if="error" class="code-error">
                  {{ error }}
                </div>
              </transition>

              <!-- Submit button -->
              <button
                class="code-submit"
                :class="{ disabled: !code || isLoading }"
                @click="handleSubmit"
              >
                <span v-if="!isLoading">Unlock</span>
                <span v-else class="loading-spinner"></span>
              </button>

              <!-- Help text below button -->
              <div class="code-help">
                <p class="code-help-text">
                  Need help? Email
                  <a
                    href="mailto:tavishi@travelwithtern.com"
                    class="code-note-link"
                    >tavishi@travelwithtern.com</a
                  >.
                </p>
              </div>
            </div>
          </transition>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import * as amplitude from '@amplitude/analytics-browser';
import introMainPng from '@/assets/intro/main.png';
import introMainMobilePng from '@/assets/intro/main_mobile.png';

const CORRECT_PASSWORD = 'firstoftilly'; // Change this to your desired code

export default {
  name: 'TravelSplashScreen',
  emits: ['splash-complete', 'authenticated'],
  data() {
    return {
      showSplash: true,
      timeoutId: null,
      showPasswordInput: false,
      code: '',
      error: '',
      isFocused: false,
      isLoading: false,
      isAuthenticated: false,
    };
  },
  mounted() {
    // Check if already authenticated
    this.isAuthenticated =
      sessionStorage.getItem('travel_authenticated') === 'true';

    // Show splash screen
    this.startSplash();
  },
  beforeUnmount() {
    // Clean up timeout if component unmounts before splash finishes
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
    }
  },
  methods: {
    startSplash() {
      // Reset splash screen state
      this.showSplash = true;

      // Clear any existing timeout
      if (this.timeoutId) {
        clearTimeout(this.timeoutId);
      }

      if (this.isAuthenticated) {
        // If authenticated, just show splash and complete after 2.8 seconds
        this.timeoutId = setTimeout(() => {
          this.showSplash = false;
          this.timeoutId = null;
          this.$emit('splash-complete');
        }, 2800);
      } else {
        // If not authenticated, show code input after logo animations (around 1.5s)
        this.timeoutId = setTimeout(() => {
          this.showPasswordInput = true;
          // Warm the intro hero image while the user types — it'll be
          // ready by the time `handleAuthenticated` mounts IntroSection.
          this.preloadIntroAssets();
          // Focus input after it appears
          this.$nextTick(() => {
            setTimeout(() => {
              if (this.$refs.codeInput) {
                this.$refs.codeInput.focus();
              }
            }, 300);
          });
        }, 1500);
      }
    },
    preloadIntroAssets() {
      if (typeof window === 'undefined' || typeof document === 'undefined') {
        return;
      }
      const isMobile = window.matchMedia('(max-width: 767px)').matches;
      const href = isMobile ? introMainMobilePng : introMainPng;
      if (document.head.querySelector(`link[rel="preload"][href="${href}"]`)) {
        return;
      }
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'image';
      link.href = href;
      document.head.appendChild(link);
    },
    handleInput() {
      // Clear error on input
      if (this.error) {
        this.error = '';
      }
    },
    async handleSubmit() {
      if (!this.code || this.isLoading) return;

      this.isLoading = true;
      this.error = '';
      amplitude.track('Password Submitted', {
        source: 'splash_screen',
        code: this.code,
      });
      // Simulate a brief delay for smooth UX
      await new Promise((resolve) => setTimeout(resolve, 300));

      if (this.code.toLowerCase() === CORRECT_PASSWORD) {
        // Store authentication
        sessionStorage.setItem('travel_authenticated', 'true');
        this.isAuthenticated = true;

        // Hide code input
        this.showPasswordInput = false;

        // Emit authenticated event
        this.$emit('authenticated');

        // Wait a moment, then complete splash
        setTimeout(() => {
          this.showSplash = false;
          this.$emit('splash-complete');
        }, 500);
      } else {
        this.error =
          'Wrong code. It’s in the email subject “Tern is now Tilly ✨” or email tavishi@travelwithtern.com and we’ll resend it.';
        this.code = '';
        this.isLoading = false;

        // Shake animation
        this.$refs.codeInput?.classList.add('shake');
        setTimeout(() => {
          this.$refs.codeInput?.classList.remove('shake');
        }, 500);
      }
    },
  },
};
</script>

<style scoped>
.splash-screen {
  position: fixed;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: radial-gradient(
      ellipse 70% 50% at 15% 18%,
      rgba(88, 168, 225, 0.12) 0%,
      transparent 60%
    ),
    radial-gradient(
      ellipse 60% 45% at 85% 82%,
      rgba(253, 173, 0, 0.1) 0%,
      transparent 55%
    ),
    radial-gradient(
      ellipse 50% 40% at 78% 12%,
      rgba(244, 132, 108, 0.07) 0%,
      transparent 60%
    ),
    linear-gradient(180deg, #fffdf9 0%, #fafbfe 100%);
  z-index: 2000;
  overflow: hidden;
}

/* Matches Navbar.vue's waitlist-page nav: centered items, same gap
   progression, same type scale, same dark-gray text. */
.splash-nav {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 1.25rem 1.5rem;
  z-index: 10;
  gap: 1rem;
}

@media (min-width: 640px) {
  .splash-nav {
    gap: 1.5rem;
  }
}
@media (min-width: 768px) {
  .splash-nav {
    gap: 2rem;
  }
}

.splash-visa-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  font-family: 'AirbnbCereal_W_Md', sans-serif;
  font-size: 0.875rem;
  font-weight: 500;
  text-decoration: none;
  color: rgba(0, 0, 0, 0.6);
  padding: 0.5rem 0;
  transition: color 0.2s ease;
  white-space: nowrap;
}
.splash-visa-chip svg {
  opacity: 0.75;
}
.splash-visa-chip:hover {
  color: #58a8e1;
}

.splash-nav-link {
  font-size: 0.875rem;
  font-weight: 500;
  color: #1f2937;
  text-decoration: none;
  transition: opacity 0.2s ease;
  padding: 0.5rem 0;
  white-space: nowrap;
}

@media (min-width: 640px) {
  .splash-nav-link {
    font-size: 1rem;
  }
}

.splash-nav-link:hover {
  opacity: 0.8;
}

.splash-logo-container {
  position: relative;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2rem;
}

/* Text animation */
.splash-text {
  font-size: 2rem;
  font-weight: 600;
  letter-spacing: -0.02em;
  line-height: 1.25;
  opacity: 0;
  animation: text-fade-in 0.8s ease-out 0.3s both;
}
.splash-text-line {
  display: block;
}

.text-tern {
  color: #1a1a1a;
}

.text-is {
  color: #666;
  font-weight: 400;
}

.text-tilly {
  color: #58a8e1;
}

.logo-transition-container {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1.5rem;
  position: relative;
}

/* Logo animations */
.splash-logo {
  width: 140px;
  max-width: 30vw;
  opacity: 0;
  transform: translateY(20px) scale(0.9);
  animation: logo-slide-up 1.2s cubic-bezier(0.25, 0.1, 0.25, 1) both;
}

.splash-logo-tern {
  animation-delay: 0.5s;
}

.splash-logo-tilly {
  animation-delay: 0.7s;
}

@keyframes logo-slide-up {
  0% {
    opacity: 0;
    transform: translateY(20px) scale(0.9);
  }
  60% {
    opacity: 1;
    transform: translateY(-5px) scale(1.05);
  }
  80% {
    transform: translateY(2px) scale(0.98);
  }
  100% {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@keyframes text-fade-in {
  0% {
    opacity: 0;
    transform: translateY(10px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Arrow container */
.arrow-container {
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  animation: arrow-fade-in 0.6s ease-out 0.9s both;
}

.arrow {
  width: 32px;
  height: 32px;
  color: #58a8e1;
  opacity: 0.6;
}

@keyframes arrow-fade-in {
  0% {
    opacity: 0;
    transform: translateX(-10px);
  }
  100% {
    opacity: 1;
    transform: translateX(0);
  }
}

/* Sparkle wrapper to hold multiple points */
.sparkle-wrapper {
  position: absolute;
  top: -30px;
  left: 50%;
  width: 100px;
  height: 100px;
  transform: translateX(-50%);
  pointer-events: none;
}

/* Base sparkle style */
.splash-sparkle {
  position: absolute;
  opacity: 0;
  filter: drop-shadow(0 0 12px #c3e5ff) drop-shadow(0 0 16px #fff);
}

/* First sparkle: big burst */
.splash-sparkle-1 {
  top: 0;
  left: 50%;
  width: 40px;
  animation: sparkle-burst 1.2s ease-out 1s both;
}

/* Second sparkle: smaller shimmer */
.splash-sparkle-2 {
  top: 20px;
  left: 70%;
  width: 24px;
  animation: sparkle-shimmer 1s ease-out 1.3s both;
}

@keyframes sparkle-burst {
  0% {
    opacity: 0;
    transform: translate(-50%, 0) scale(0.2) rotate(-30deg);
  }
  30% {
    opacity: 1;
    transform: translate(-50%, -20px) scale(1.8) rotate(0deg);
  }
  60% {
    transform: translate(-50%, -10px) scale(1.4) rotate(15deg);
  }
  100% {
    opacity: 0;
    transform: translate(-50%, 0) scale(0.8) rotate(30deg);
  }
}

@keyframes sparkle-shimmer {
  0% {
    opacity: 0;
    transform: translate(-50%, 0) scale(0.1) rotate(0deg);
  }
  50% {
    opacity: 0.8;
    transform: translate(-50%, -8px) scale(1) rotate(10deg);
  }
  100% {
    opacity: 0;
    transform: translate(-50%, 0) scale(0.5) rotate(-10deg);
  }
}

/* Transitions */
.splash-fade-leave-active {
  transition: opacity 0.5s ease;
}
.splash-fade-leave-to {
  opacity: 0;
}

@media (max-width: 600px) {
  .splash-text {
    font-size: 1.5rem;
  }

  .splash-logo {
    width: 100px;
  }

  .arrow {
    width: 24px;
    height: 24px;
  }

  .logo-transition-container {
    gap: 1rem;
  }

  .sparkle-wrapper {
    width: 70px;
    height: 70px;
    top: -20px;
  }

  .splash-sparkle-1 {
    width: 28px;
  }

  .splash-sparkle-2 {
    width: 16px;
  }
}

/* Password Section */
.code-section {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 1.25rem;
  margin-top: 2.5rem;
  width: 100%;
  max-width: 440px;
  padding: 2.5rem 2.25rem 2rem;
  background: rgba(255, 255, 255, 0.7);
  border: 1px solid rgba(0, 0, 0, 0.05);
  border-radius: 20px;
  backdrop-filter: blur(18px);
  -webkit-backdrop-filter: blur(18px);
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04),
    0 12px 32px rgba(15, 23, 42, 0.06);
}

.code-note {
  width: 100%;
  margin-bottom: 0.5rem;
  opacity: 0;
  animation: note-fade-in 0.8s ease-out 0.2s both;
}

.code-note-text {
  font-size: 0.875rem;
  line-height: 1.6;
  color: rgba(0, 0, 0, 0.65);
  text-align: center;
  font-weight: 400;
  letter-spacing: 0.01em;
}

.code-note-subject {
  color: #1a1a1a;
  font-weight: 600;
  white-space: nowrap;
}
.code-note-link {
  color: #58a8e1;
  text-decoration: none;
  font-weight: 500;
  transition: all 0.2s ease;
  border-bottom: 1px solid transparent;
}

.code-note-link:hover {
  color: #7bb8e8;
  border-bottom-color: #7bb8e8;
}

.code-help {
  width: 100%;
  margin-top: 0.5rem;
  opacity: 0;
  animation: help-fade-in 0.8s ease-out 0.8s both;
}

.code-help-text {
  font-size: 0.8125rem;
  line-height: 1.5;
  color: rgba(0, 0, 0, 0.55);
  text-align: center;
  font-weight: 400;
  letter-spacing: 0.01em;
}

.code-notice {
  width: 100%;
  margin-top: 1rem;
  opacity: 0;
  animation: notice-fade-in 0.8s ease-out 1s both;
}

@keyframes notice-fade-in {
  0% {
    opacity: 0;
    transform: translateY(8px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes note-fade-in {
  0% {
    opacity: 0;
    transform: translateY(10px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes help-fade-in {
  0% {
    opacity: 0;
    transform: translateY(8px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}

.code-input-wrapper {
  position: relative;
  width: 100%;
  opacity: 0;
  animation: input-fade-in 0.8s ease-out 0.4s both;
}

@keyframes input-fade-in {
  0% {
    opacity: 0;
    transform: translateY(15px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}

.code-input {
  width: 100%;
  padding: 1rem 1.25rem;
  font-size: 1.125rem;
  font-weight: 400;
  letter-spacing: 0.01em;
  color: #1a1a1a;
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 12px;
  outline: none;
  transition: all 0.3s cubic-bezier(0.25, 0.1, 0.25, 1);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06), inset 0 1px 2px rgba(0, 0, 0, 0.02);
}

.code-input::placeholder {
  color: rgba(0, 0, 0, 0.35);
  font-weight: 300;
}

.code-input:focus {
  background: rgba(255, 255, 255, 0.95);
  border-color: rgba(88, 168, 225, 0.4);
  box-shadow: 0 4px 16px rgba(88, 168, 225, 0.15), 0 2px 8px rgba(0, 0, 0, 0.06),
    inset 0 1px 2px rgba(0, 0, 0, 0.02);
  transform: translateY(-1px);
}

.code-input.shake {
  animation: shake 0.5s cubic-bezier(0.36, 0.07, 0.19, 0.97) both;
}

@keyframes shake {
  10%,
  90% {
    transform: translateX(-4px);
  }
  20%,
  80% {
    transform: translateX(4px);
  }
  30%,
  50%,
  70% {
    transform: translateX(-4px);
  }
  40%,
  60% {
    transform: translateX(4px);
  }
}

.code-error {
  color: #ef4444;
  font-size: 0.875rem;
  font-weight: 400;
  text-align: center;
  margin-top: -1rem;
  padding: 0.5rem 1rem;
  background: rgba(239, 68, 68, 0.08);
  border-radius: 8px;
  border: 1px solid rgba(239, 68, 68, 0.2);
}

.code-submit {
  width: 100%;
  padding: 1rem 2rem;
  font-size: 1rem;
  font-weight: 500;
  letter-spacing: 0.02em;
  color: white;
  background: linear-gradient(135deg, #58a8e1 0%, #7bb8e8 100%);
  border: none;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.25, 0.1, 0.25, 1);
  box-shadow: 0 4px 16px rgba(88, 168, 225, 0.3),
    0 2px 8px rgba(88, 168, 225, 0.2);
  position: relative;
  overflow: hidden;
  opacity: 0;
  animation: button-fade-in 0.8s ease-out 0.6s both;
}

@keyframes button-fade-in {
  0% {
    opacity: 0;
    transform: translateY(10px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}

.code-submit::before {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.2) 0%,
    transparent 100%
  );
  opacity: 0;
  transition: opacity 0.3s;
}

.code-submit:hover:not(.disabled)::before {
  opacity: 1;
}

.code-submit:hover:not(.disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 24px rgba(88, 168, 225, 0.4),
    0 4px 12px rgba(88, 168, 225, 0.3);
}

.code-submit:active:not(.disabled) {
  transform: translateY(0);
  box-shadow: 0 2px 8px rgba(88, 168, 225, 0.3),
    0 1px 4px rgba(88, 168, 225, 0.2);
}

.code-submit.disabled {
  background: #e5e7eb;
  color: rgba(0, 0, 0, 0.35);
  box-shadow: none;
  cursor: not-allowed;
  transform: none;
}

.loading-spinner {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Password animations */
.code-slide-up-enter-active {
  transition: all 0.8s cubic-bezier(0.25, 0.1, 0.25, 1);
}

.code-slide-up-enter-from {
  opacity: 0;
  transform: translateY(30px) scale(0.95);
}

.code-slide-up-leave-active {
  transition: all 0.5s cubic-bezier(0.25, 0.1, 0.25, 1);
}

.code-slide-up-leave-to {
  opacity: 0;
  transform: translateY(-20px) scale(0.98);
}

.error-fade-enter-active,
.error-fade-leave-active {
  transition: all 0.3s ease;
}

.error-fade-enter-from,
.error-fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

@media (max-width: 600px) {
  /* Anchor content toward the upper-third on tall mobile viewports so the
     glass card doesn't float in a sea of whitespace. */
  .splash-screen {
    justify-content: flex-start;
    padding-top: 14vh;
  }
  .splash-logo-container {
    gap: 1.5rem;
  }
  .splash-text {
    font-size: 1.5rem;
  }

  .code-section {
    max-width: 360px;
    gap: 1.1rem;
    margin-top: 1.75rem;
    padding: 2rem 1.5rem 1.75rem;
    border-radius: 18px;
  }

  .code-note-text {
    font-size: 0.8125rem;
    line-height: 1.5;
  }

  .code-help-text {
    font-size: 0.75rem;
    line-height: 1.4;
    word-break: break-word;
  }

  .code-notice {
    margin-top: 0.75rem;
  }

  .splash-nav {
    padding: 1rem 1.5rem;
  }

  .splash-nav-link {
    font-size: 0.875rem;
    padding: 0.5rem 0;
  }

  .code-input {
    font-size: 1rem;
    padding: 0.875rem 1rem;
  }

  .code-submit {
    padding: 0.875rem 1.5rem;
    font-size: 0.9375rem;
  }
}
</style>
